// Where the money comes from, geographically.
//
// The rows are `GET /api/reports/revenue-by-region`, and "revenue" there is the
// BILLING SCHEDULE — items sent or paid, actual where one was recorded — which
// is character for character the partner rollup's `billed_to_date_*`. The two
// reports are therefore the same money cut two ways and must total the same,
// and this screen is careful not to quietly break that: the unlocated bucket is
// rendered like any other row and included in the footer's total, because
// dropping it is precisely what would make this screen disagree with the
// partner report while both said "billed to date".
//
// THE UNLOCATED ROW IS NOT A PLACE. It is the count of deals nobody has said
// anything about, and it is the only row on this screen that is an instruction
// rather than an observation — so it is marked as such, sorted last by the API,
// and links straight to the deals list filtered to exactly the deals it counts.
// A number that accuses without offering the list is a number people learn to
// scroll past.
//
// The map of these deals is its own screen, `/map`: this one is the table.

import { useCallback, useMemo, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Currency, RevenueByRegionGroupBy } from "@crm/shared";
import type { Partner, ReportScope, RevenueByRegionReport, RevenueByRegionRow } from "@crm/shared";
import styles from "./RegionsPage.module.css";
import { regionStateToSearch, searchToRegionState, type RegionsSearch } from "./regionsSearch.js";
import { UnresolvedCount } from "../../components/SplitSource.js";
import { Badge } from "../../design/Badge.js";
import { Card, CardHeader } from "../../design/Card.js";
import { ProblemMessage } from "../../design/Feedback.js";
import { FxFootnote } from "../../design/FxFootnote.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { countryName } from "../../format/countries.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { formatCount, formatMoney } from "../../format/money.js";
import {
  usePartnerOptions,
  useRevenueByRegionReport,
  type RegionQueryState,
} from "../../api/queries.js";

const ANY_PARTNER = "__any__";

const GROUP_BY_OPTIONS: ReadonlyArray<SegmentedOption<RevenueByRegionGroupBy>> = [
  { value: "country", label: "Country", title: "One row per country" },
  { value: "region", label: "Region", title: "Each country split by state or province" },
];

/**
 * The same two questions the partner rollup asks, in the same words — a family
 * of partners is one relationship, and asking about Harris almost always means
 * the Harris family.
 */
const SCOPES: ReadonlyArray<SegmentedOption<ReportScope>> = [
  { value: "direct", label: "This company" },
  { value: "rolled_up", label: "Including owned companies" },
];

export function RegionsPage(): ReactNode {
  const search = useSearch({ from: "/regions" });
  const navigate = useNavigate();
  const reportCurrency = useReportCurrency();
  const state = useMemo(
    () => searchToRegionState(search, reportCurrency),
    [search, reportCurrency],
  );

  const setState = useCallback(
    (next: RegionQueryState) => {
      void navigate({
        to: "/regions",
        search: regionStateToSearch(next) as RegionsSearch,
      });
    },
    [navigate],
  );

  const report = useRevenueByRegionReport(state);
  const partners = usePartnerOptions();

  usePageChrome({
    title: "Regions",
  });

  // A FAILED REPORT IS NOT A LOADING ONE. The tiles sit outside the panels'
  // boundaries, and their hints are assertions about the data — "Gross across
  // 0 places" is a claim, not a blank. So a failure hides them and puts the
  // API's own sentence on the page once, in their place: the FX refusal names
  // the dates a person has to add rates for, and a reader who cannot see it
  // has nothing to act on.
  const problem = report.error;
  const failed = problem !== null && problem !== undefined;

  return (
    <div className={styles.page}>
      {failed ? (
        <ProblemMessage
          error={problem}
          action="load the revenue-by-region report"
          onRetry={() => void report.refetch()}
        />
      ) : (
        <HeadlineTiles report={report.data} />
      )}

      <div className={styles.toolbar}>
        <Segmented
          label="Grouping"
          variant="inset"
          options={GROUP_BY_OPTIONS}
          value={state.groupBy}
          onChange={(groupBy) => setState({ ...state, groupBy })}
        />

        <PartnerFilter
          partners={partners.data?.items ?? []}
          partnerId={state.partnerId}
          onChange={(partnerId) => setState({ ...state, partnerId })}
        />

        {/* Offered only alongside a partner: with no partner chosen the report
            covers everything, and the two scopes return the same rows. A
            control that cannot change the answer is worse than no control. */}
        {state.partnerId !== null && (
          <Segmented
            label="Partner scope"
            variant="inset"
            options={SCOPES}
            value={state.scope}
            onChange={(scope) => setState({ ...state, scope })}
          />
        )}
      </div>

      {/* ONE MESSAGE PER FAILURE. The message is above; the table only draws
          when there is something to draw in it. */}
      {!failed && (
        <>
          <Card>
            <CardHeader
              title={state.groupBy === "country" ? "By country" : "By region"}
              description="Schedule items sent or paid — actual where one was recorded, the estimate where it was not. The same money the partner rollup reports as billed to date, so the two totals reconcile."
            />
            <QueryBoundary query={report} label="the revenue-by-region report">
              {(data) => <RegionTable report={data} />}
            </QueryBoundary>
          </Card>
        </>
      )}
    </div>
  );
}

function PartnerFilter({
  partners,
  partnerId,
  onChange,
}: {
  partners: readonly Partner[];
  partnerId: string | null;
  onChange: (partnerId: string | null) => void;
}): ReactNode {
  return (
    <select
      className={styles.control}
      aria-label="Partner"
      value={partnerId ?? ANY_PARTNER}
      onChange={(event) => {
        const picked = event.target.value;
        onChange(picked === ANY_PARTNER ? null : picked);
      }}
    >
      <option value={ANY_PARTNER}>Every partner</option>
      {partners.map((partner) => (
        <option key={partner.id} value={partner.id}>
          {partner.name}
        </option>
      ))}
    </select>
  );
}

/**
 * The four figures above the table. The fourth is the unlocated bucket, and it
 * is a headline on purpose: it is the one number here that says how much of the
 * rest can be trusted as a picture of anywhere.
 */
function HeadlineTiles({ report }: { report: RevenueByRegionReport | undefined }): ReactNode {
  const unlocated = report?.rows.find((row) => row.is_unlocated) ?? null;
  const places = report === undefined ? 0 : report.rows.filter((row) => !row.is_unlocated).length;

  return (
    <StatTileRow>
      <StatTile
        label="Billed to date"
        value={
          report === undefined
            ? null
            : formatMoney(report.totals.billed_to_date_gross_minor, report.currency)
        }
        hint={`Gross across ${formatCount(places)} place${places === 1 ? "" : "s"}, the unlocated bucket included.`}
      />
      <StatTile
        label="Our share billed"
        value={
          report === undefined
            ? null
            : formatMoney(report.totals.billed_to_date_our_share_minor, report.currency)
        }
        // The split is derived until a bill goes out and frozen at that moment
        // (migration 0013), so a BILLED figure cannot lose a row to an
        // unresolvable split — but the contracted figures beside it can, and
        // the count is the only thing that says so.
        hint={
          report === undefined || report.totals.unresolved_item_count === 0
            ? "After each bill's split — frozen when it went out."
            : `After each charge's split. ${formatCount(report.totals.unresolved_item_count)} charge${report.totals.unresolved_item_count === 1 ? "" : "s"} here cannot resolve a split at all, and are left out of every our-share figure below.`
        }
      />
      <StatTile
        label="Customers"
        value={report === undefined ? null : formatCount(report.totals.customer_count)}
        hint="Counted per place: a customer with deals in two regions is a customer in each of them."
      />
      {/* Zero when nothing is unlocated, not blank: "we have placed everything"
          is an answer, and an empty tile would read as one still loading. */}
      <StatTile
        label="Not located"
        value={
          report === undefined
            ? null
            : formatMoney(unlocated?.billed_to_date_gross_minor ?? 0, report.currency)
        }
        hint={
          unlocated === null
            ? "Every deal with billing history has a country recorded."
            : `${formatCount(unlocated.deal_count)} deal${unlocated.deal_count === 1 ? "" : "s"} with no country recorded.`
        }
      />
    </StatTileRow>
  );
}

function RegionTable({ report }: { report: RevenueByRegionReport }): ReactNode {
  return (
    <>
      <Table label={`Billed and contracted revenue by ${report.params.group_by}`}>
        <thead>
          <tr>
            <Th>Place</Th>
            <Th numeric>Deals</Th>
            <Th numeric>Customers</Th>
            <Th numeric>Billed</Th>
            <Th numeric>Contracted</Th>
            <Th numeric>Still to bill</Th>
            <Th numeric>Our share</Th>
            {/* How many of this row's deals a pin actually speaks for. The
                coordinate itself is on the map now; what the table still owes
                the reader is how complete that map is. */}
            <Th numeric>Plotted</Th>
          </tr>
        </thead>
        <tbody>
          {report.rows.length === 0 ? (
            <EmptyRow
              colSpan={8}
              title="No revenue to place yet"
              description="This report counts the charges of deals that have started to bill. None has, for the partners in scope, so there is nothing to cut by geography."
            />
          ) : (
            report.rows.map((row) => <RegionRow key={row.key} row={row} report={report} />)
          )}
        </tbody>
        {report.rows.length > 0 && (
          <tfoot>
            <tr>
              <Td>Total</Td>
              <Td numeric mono muted>
                {formatCount(report.totals.deal_count)}
              </Td>
              {/* Summed, but it is "customers per place" — a customer selling
                  into two regions is counted in each row and so twice here. */}
              <Td
                numeric
                mono
                muted
                title="A customer with deals in two places is counted in each of them, so this exceeds the number of distinct customers."
              >
                {formatCount(report.totals.customer_count)}
              </Td>
              <Td numeric mono>
                {formatMoney(report.totals.billed_to_date_gross_minor, report.currency, {
                  omitCurrencyCode: true,
                })}
              </Td>
              {/* Not known the moment one place's total is not known: a sum
                  that quietly left an unknowable figure out would read as a
                  complete total that happens to be smaller. */}
              <Td numeric mono>
                <UnknownableMoney
                  minor={report.totals.contracted_gross_minor}
                  currency={report.currency}
                />
              </Td>
              <Td numeric mono>
                <UnknownableMoney
                  minor={
                    report.totals.contracted_gross_minor === null
                      ? null
                      : report.totals.contracted_gross_minor -
                        report.totals.billed_to_date_gross_minor
                  }
                  currency={report.currency}
                />
              </Td>
              <Td numeric mono strong>
                {formatMoney(report.totals.billed_to_date_our_share_minor, report.currency, {
                  omitCurrencyCode: true,
                })}
                <UnresolvedCount count={report.totals.unresolved_item_count} of="charges" />
              </Td>
              <Td />
            </tr>
          </tfoot>
        )}
      </Table>
      <FxFootnote currency={report.currency} fx={report.fx} generatedAt={report.generated_at} />
    </>
  );
}

function RegionRow({
  row,
  report,
}: {
  row: RevenueByRegionRow;
  report: RevenueByRegionReport;
}): ReactNode {
  return (
    <tr className={row.is_unlocated ? styles.unlocatedRow : undefined}>
      <Td strong>{row.is_unlocated ? <UnlocatedPlace row={row} /> : <Place row={row} />}</Td>
      <Td numeric mono muted>
        {formatCount(row.deal_count)}
      </Td>
      <Td numeric mono muted>
        {formatCount(row.customer_count)}
      </Td>
      <Td numeric mono>
        {formatMoney(row.billed_to_date_gross_minor, report.currency, { omitCurrencyCode: true })}
      </Td>
      <Td numeric mono>
        <UnknownableMoney minor={row.contracted_gross_minor} currency={report.currency} />
      </Td>
      <Td numeric mono muted>
        <UnknownableMoney
          minor={
            row.contracted_gross_minor === null
              ? null
              : row.contracted_gross_minor - row.billed_to_date_gross_minor
          }
          currency={report.currency}
        />
      </Td>
      {/* Both `*_our_share_minor` figures exclude the items whose split does
          not resolve while both `*_gross_minor` figures include them, so a
          non-zero count is the difference between "we keep this share of the
          region" and "we keep this share of the part of it that prices
          itself". */}
      <Td numeric mono strong>
        {formatMoney(row.billed_to_date_our_share_minor, report.currency, {
          omitCurrencyCode: true,
        })}
        <UnresolvedCount count={row.unresolved_item_count} of="charges" />
      </Td>
      <Td numeric mono muted nowrap>
        <Plotted row={row} />
      </Td>
    </tr>
  );
}

/**
 * The API's `label` verbatim — it is declared as "the same string every
 * consumer shows", and re-deriving it here from `country`/`region` is how two
 * screens end up disagreeing about what a bucket is called. The country's NAME
 * rides underneath, because a table of alpha-2 codes is a table you have to
 * translate as you read it.
 */
function Place({ row }: { row: RevenueByRegionRow }): ReactNode {
  const name = row.country === null ? null : countryName(row.country);
  return (
    <>
      <span>{row.label}</span>
      {name !== null && name !== row.label && <span className={styles.subLabel}>{name}</span>}
    </>
  );
}

/**
 * The one row that is a data-quality fact rather than a place, and the only one
 * that is a link: it goes to exactly the deals it counts, which is what turns
 * "some of this revenue is nowhere" into an afternoon's work.
 */
function UnlocatedPlace({ row }: { row: RevenueByRegionRow }): ReactNode {
  return (
    <>
      <span className={styles.unlocatedPlace}>
        <Badge shape="tag" tone="warn">
          Not a place
        </Badge>
        <Link to="/deals" search={{ location: "unlocated" }}>
          {row.label}
        </Link>
      </span>
      <span className={styles.subLabel}>
        Counted here so this total matches the partner rollup&rsquo;s. Fix the deals to move it.
      </span>
    </>
  );
}

/**
 * How much of this row the map is actually drawing.
 *
 * The raw coordinate used to live in this column, back when nothing rendered
 * it. Now that the map does, the useful fact is not the number — it is how many
 * of the row's deals have one, because a place shaded from nine deals with two
 * pins on it is showing far less than it looks like it is. `n of m` says that
 * in the width of a table cell, and "none" is the worklist entry that gets a
 * coordinate keyed in.
 */
function Plotted({ row }: { row: RevenueByRegionRow }): ReactNode {
  if (row.deal_count === 0) return <span className={styles.noCoordinates}>—</span>;
  if (row.located_deal_count === 0) {
    return <span className={styles.noCoordinates}>none</span>;
  }
  return <span>{`${formatCount(row.located_deal_count)} of ${formatCount(row.deal_count)}`}</span>;
}

/**
 * A figure that may not be knowable, and the words for when it is not.
 *
 * A deal's contracted value is the whole of what its clauses will bill, and it
 * stops being knowable the moment one active clause is metered: how much a
 * metered clause is worth is however many units the facts turn out to carry.
 * So this renders "not known" and NEVER a zero — a zero here would say the
 * place is worth nothing, which is the opposite of what is true.
 */
function UnknownableMoney({
  minor,
  currency,
}: {
  minor: number | null;
  currency: Currency;
}): ReactNode {
  if (minor === null) return <span className={styles.unknown}>not known</span>;
  return <>{formatMoney(minor, currency, { omitCurrencyCode: true })}</>;
}
